/**
 * useUserAvatar Hook
 * Returns the current user's avatar from UserProfileContext (single source of truth).
 * Stays in sync when the user updates their avatar (e.g. in menu modal); no separate fetch.
 */

import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useUserProfile } from '../../shared/contexts/UserProfileContext';
import { normalizeAvatarId, type AvatarId } from '../../../constants/avatarIds';

/**
 * Hook to get the current user's avatar.
 * Returns the user's avatarId once profile is loaded (always a valid {@link AvatarId}), or undefined while loading/unauthenticated.
 * Reads from UserProfileContext so avatar updates (e.g. from menu picker) are reflected everywhere.
 */
export const useUserAvatar = (): AvatarId | undefined => {
  const { isLoaded, isSignedIn } = useSupabaseAuth();
  const { profile, isLoading } = useUserProfile();

  if (!isLoaded || !isSignedIn) return undefined;
  if (isLoading || profile === null) return undefined;
  return normalizeAvatarId(profile.avatarId);
};


